import { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Input, Paragraph, Spinner, Text, XStack, YStack } from 'tamagui';
import type { ReviewQueueItem } from '@mrrmlab/api-client';
import { useReviewQueue, useConfirmTag, useRemoveMessageTag, useAssignTag, useMailTags } from '../mail/hooks';

type Phase = 'start' | 'reviewing' | 'done';
type TagAction = 'keep' | 'remove';

const BATCH_OPTIONS = [10, 50, null] as const;
type BatchOption = (typeof BATCH_OPTIONS)[number];

function emptyActions(item: ReviewQueueItem): Record<string, TagAction> {
  return Object.fromEntries(item.tags.map((t) => [t.tagId, 'keep' as TagAction]));
}

export function MailReviewRoute() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('start');
  const [batch, setBatch] = useState<BatchOption>(10);
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [index, setIndex] = useState(0);
  const [tagActions, setTagActions] = useState<Record<string, TagAction>>({});
  const [pendingNewTags, setPendingNewTags] = useState<string[]>([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [confirmed, setConfirmed] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: queueData, isLoading } = useReviewQueue(batch ?? undefined);
  const { data: allTags } = useMailTags();
  const { mutateAsync: confirmTag } = useConfirmTag();
  const { mutateAsync: removeTag } = useRemoveMessageTag();
  const { mutateAsync: assignTag } = useAssignTag();

  function resetCard(item: ReviewQueueItem) {
    setTagActions(emptyActions(item));
    setPendingNewTags([]);
    setNewTagInput('');
    setSubmitError(null);
  }

  function startSession() {
    if (!queueData?.items.length) return;
    const firstItem = queueData.items[0]!;
    setItems(queueData.items);
    setIndex(0);
    setConfirmed(0);
    setSkipped(0);
    resetCard(firstItem);
    setPhase('reviewing');
  }

  function advance(nextIndex: number, nextItems: ReviewQueueItem[]) {
    if (nextIndex >= nextItems.length) {
      setPhase('done');
    } else {
      setIndex(nextIndex);
      resetCard(nextItems[nextIndex]!);
    }
  }

  const toggleTag = useCallback((tagId: string) => {
    setTagActions((prev) => ({ ...prev, [tagId]: prev[tagId] === 'keep' ? 'remove' : 'keep' }));
  }, []);

  function addNewTag() {
    const name = newTagInput.trim();
    if (!name || pendingNewTags.includes(name)) return;
    setPendingNewTags((prev) => [...prev, name]);
    setNewTagInput('');
  }

  function removeNewTag(name: string) {
    setPendingNewTags((prev) => prev.filter((t) => t !== name));
  }

  async function handleSubmit(item: ReviewQueueItem) {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await Promise.all(
        item.tags.map((t) =>
          tagActions[t.tagId] === 'remove'
            ? removeTag({ messageId: item.messageId, tagId: t.tagId })
            : confirmTag({ messageId: item.messageId, tagId: t.tagId }),
        ),
      );
      for (const name of pendingNewTags) {
        await assignTag({ messageId: item.messageId, name, status: 'confirmed' });
      }
      setConfirmed((c) => c + 1);
      advance(index + 1, items);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  }

  function handleSkip() {
    setSkipped((s) => s + 1);
    advance(index + 1, items);
  }

  if (phase === 'start') {
    return (
      <YStack padding="$4" gap="$5" maxWidth={640}>
        <Text fontSize="$7" fontWeight="700">Mail-Review</Text>
        {isLoading ? (
          <Spinner />
        ) : !queueData?.total ? (
          <Paragraph color="$color10">Keine unbestätigten Tags vorhanden.</Paragraph>
        ) : (
          <>
            <Paragraph color="$color10">
              {queueData.total} {queueData.total === 1 ? 'Mail wartet' : 'Mails warten'} auf Review.
            </Paragraph>
            <YStack gap="$2">
              <Text fontWeight="600">Anzahl reviewen:</Text>
              <XStack gap="$2">
                {BATCH_OPTIONS.map((opt) => (
                  <Button
                    key={String(opt)}
                    size="$3"
                    variant={batch === opt ? undefined : 'outlined'}
                    onPress={() => setBatch(opt)}
                  >
                    {opt === null ? 'Alle' : String(opt)}
                  </Button>
                ))}
              </XStack>
            </YStack>
            <Button onPress={startSession} disabled={!queueData?.items.length}>
              Jetzt starten
            </Button>
          </>
        )}
      </YStack>
    );
  }

  if (phase === 'done') {
    return (
      <YStack padding="$4" gap="$4" maxWidth={640}>
        <Text fontSize="$7" fontWeight="700">Session abgeschlossen</Text>
        <YStack gap="$1">
          <Text>Bestätigt: <Text fontWeight="700">{confirmed}</Text></Text>
          <Text>Übersprungen: <Text fontWeight="700">{skipped}</Text></Text>
        </YStack>
        <XStack gap="$2">
          <Button onPress={() => setPhase('start')}>Neue Session</Button>
          <Button chromeless onPress={() => navigate({ to: '/mail' })}>← Zurück zur Mail-Übersicht</Button>
        </XStack>
      </YStack>
    );
  }

  const item = items[index];
  if (!item) return null;

  const assignedNames = new Set([...item.tags.map((t) => t.name.toLowerCase()), ...pendingNewTags.map((n) => n.toLowerCase())]);
  const inputQuery = newTagInput.trim().toLowerCase();
  const tagSuggestions = (allTags ?? []).filter(
    (t) => !assignedNames.has(t.name.toLowerCase()) && (!inputQuery || t.name.toLowerCase().includes(inputQuery)),
  );
  const showCreateButton = newTagInput.trim() && !assignedNames.has(newTagInput.trim().toLowerCase()) && !tagSuggestions.some((t) => t.name.toLowerCase() === newTagInput.trim().toLowerCase());

  return (
    <YStack padding="$4" gap="$4" maxWidth={640}>
      <XStack justifyContent="space-between" alignItems="center">
        <Text fontSize="$7" fontWeight="700">Mail-Review</Text>
        <Text color="$color10" fontSize="$4">{index + 1} / {items.length}</Text>
      </XStack>

      <YStack borderWidth={1} borderColor="$borderColor" borderRadius="$4" padding="$4" gap="$2">
        <Text fontSize="$5" fontWeight="700">{item.subject || '(kein Betreff)'}</Text>
        {item.fromAddr ? (
          <Text fontSize="$2" color="$color10">{item.fromAddr}</Text>
        ) : null}
        {item.snippet ? (
          <Paragraph color="$color10" fontSize="$3" marginTop="$1">{item.snippet}</Paragraph>
        ) : (
          <Text fontSize="$3" color="$color9" fontStyle="italic">Kein Textinhalt verfügbar</Text>
        )}
      </YStack>

      <YStack gap="$2">
        <Text fontSize="$2" color="$color10">Vorgeschlagene Tags — anklicken zum An-/Abwählen:</Text>
        <XStack flexWrap="wrap" gap="$2">
          {item.tags.map((t) => {
            const action = tagActions[t.tagId] ?? 'keep';
            return (
              <XStack
                key={t.tagId}
                borderRadius="$10"
                paddingHorizontal="$3"
                paddingVertical="$1"
                backgroundColor={action === 'keep' ? '$green3' : '$red3'}
                borderWidth={1}
                borderColor={action === 'keep' ? '$green7' : '$red7'}
                gap="$1"
                cursor="pointer"
                onPress={() => toggleTag(t.tagId)}
                hoverStyle={{ opacity: 0.8 }}
              >
                <Text fontSize="$3" color={action === 'keep' ? '$green11' : '$red11'} textDecorationLine={action === 'remove' ? 'line-through' : undefined}>
                  {t.name}
                </Text>
                <Text fontSize="$3" color={action === 'keep' ? '$green11' : '$red11'}>
                  {action === 'keep' ? ' ✓' : ' ✕'}
                </Text>
              </XStack>
            );
          })}
          {pendingNewTags.map((name) => (
            <XStack
              key={name}
              borderRadius="$10"
              paddingHorizontal="$3"
              paddingVertical="$1"
              backgroundColor="$blue3"
              borderWidth={1}
              borderColor="$blue7"
              gap="$1"
              cursor="pointer"
              onPress={() => removeNewTag(name)}
              hoverStyle={{ opacity: 0.8 }}
            >
              <Text fontSize="$3" color="$blue11">{name}</Text>
              <Text fontSize="$3" color="$blue11"> ✕</Text>
            </XStack>
          ))}
        </XStack>
      </YStack>

      <YStack gap="$2">
        <XStack gap="$2" alignItems="center">
          <Input
            flex={1}
            size="$3"
            placeholder="Weiteren Tag hinzufügen…"
            value={newTagInput}
            onChangeText={setNewTagInput}
            onSubmitEditing={addNewTag}
          />
          <Button size="$3" onPress={addNewTag} disabled={!newTagInput.trim()}>+</Button>
        </XStack>
        {(tagSuggestions.length > 0 || showCreateButton) && (
          <XStack flexWrap="wrap" gap="$2">
            {tagSuggestions.slice(0, 8).map((t) => (
              <Button
                key={t.id}
                size="$2"
                borderRadius="$10"
                backgroundColor="$color3"
                borderWidth={1}
                borderColor="$color7"
                onPress={() => { setPendingNewTags((prev) => [...prev, t.name]); setNewTagInput(''); }}
              >
                {t.name}
              </Button>
            ))}
            {showCreateButton && (
              <Button
                size="$2"
                borderRadius="$10"
                backgroundColor="$blue3"
                borderWidth={1}
                borderColor="$blue7"
                onPress={addNewTag}
              >
                + &ldquo;{newTagInput.trim()}&rdquo; neu
              </Button>
            )}
          </XStack>
        )}
      </YStack>

      {submitError ? <Text fontSize="$2" color="$red10">{submitError}</Text> : null}

      <XStack gap="$2">
        <Button flex={1} theme="green" onPress={() => handleSubmit(item)} disabled={submitting}>
          {submitting ? <Spinner /> : '✓ Übernehmen'}
        </Button>
        <Button flex={1} chromeless onPress={handleSkip} disabled={submitting}>
          → Überspringen
        </Button>
      </XStack>
    </YStack>
  );
}
